import type { NavEntry, NavGroup, NavSpace, SpaceKey } from './navigation';

/** A space as one person sees it: only the entries one may open, empty groups dropped. */
export interface VisibleSpace {
  key: SpaceKey;
  space: NavSpace;
  groups: NavGroup[];
  entries: NavEntry[];
}

/** Hiding only: the server checks every permission again (CLAUDE.md §4). A space with no entry
 *  left is not offered, so nobody is assumed to be an administrator (§5). */
export function visibleSpaces(
  spaces: readonly NavSpace[],
  permissions: readonly string[],
): VisibleSpace[] {
  const allowed = (e: NavEntry) => e.permission === undefined || permissions.includes(e.permission);
  return spaces
    .map((space) => {
      const groups = space.groups
        .map((g) => ({ key: g.key, entries: g.entries.filter(allowed) }))
        .filter((g) => g.entries.length > 0);
      return { key: space.key, space, groups, entries: groups.flatMap((g) => g.entries) };
    })
    .filter((s) => s.entries.length > 0);
}

/** How well a page path matches a page of the navigation: the length of the matched path,
 *  0 when it does not match. `/` matches only itself. */
function matchLength(pathname: string, to: string): number {
  if (to === '/') return pathname === '/' ? 1 : 0;
  return pathname === to || pathname.startsWith(`${to}/`) ? to.length : 0;
}

function entryMatch(pathname: string, entry: NavEntry): number {
  return Math.max(...[entry.to, ...(entry.also ?? [])].map((to) => matchLength(pathname, to)));
}

/** The entry of the open page in a space: the most specific match, so `/personnel/registry`
 *  lights «Anagrafica» and not «Accreditamento». */
export function activeEntry(pathname: string, entries: readonly NavEntry[]): NavEntry | null {
  let best: NavEntry | null = null;
  let bestLength = 0;
  for (const entry of entries) {
    const length = entryMatch(pathname, entry);
    if (length > bestLength) {
      best = entry;
      bestLength = length;
    }
  }
  return best;
}

function bestMatch(pathname: string, entries: readonly NavEntry[]): number {
  return Math.max(0, ...entries.map((e) => entryMatch(pathname, e)));
}

/** The space of the open page: the one whose entry matches it best, so a reload or the link of
 *  a notification lands in the right one (`/drones/registry` is the Segreteria's even if
 *  `/drones` is the PCA's); a page of both spaces keeps the remembered one. */
export function spaceOfPage(
  pathname: string,
  spaces: readonly VisibleSpace[],
  remembered: SpaceKey,
): SpaceKey | null {
  const scores = spaces.map((s) => ({ key: s.key, score: bestMatch(pathname, s.entries) }));
  const top = Math.max(0, ...scores.map((s) => s.score));
  const owners = top === 0 ? [] : scores.filter((s) => s.score === top).map((s) => s.key);
  if (owners.includes(remembered)) return remembered;
  if (owners[0] !== undefined) return owners[0];
  return spaces.some((s) => s.key === remembered) ? remembered : (spaces[0]?.key ?? null);
}

/** Where one lands on `/` without the operations board: the first page one may open, so whoever
 *  works only in the Segreteria starts from it (2.4 of the PCA/Segreteria proposal). */
export function landingPath(spaces: readonly VisibleSpace[]): string | null {
  if (spaces.some((s) => s.entries.some((e) => e.to === '/'))) return null;
  return spaces[0]?.entries[0]?.to ?? null;
}
